import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Search, AlertTriangle } from "lucide-react";

// DELTA 7: compact "Carrier Truth" panel. Uses the existing /copilot/carrier-intel backend
// (read-only, mock until live Fidelity access is configured). Clearly distinguishes
// Commission Posted from Commission Actually Disbursed and never fabricates results.
export default function CarrierTruthPanel({ defaultCarrier = "Fidelity" }) {
  const [q, setQ] = useState("");
  const [carrier] = useState(defaultCarrier);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const run = async () => {
    if (!q.trim()) return;
    setBusy(true); setErr(""); setRes(null);
    try {
      // Send the user's lookup term directly. Wrapping it in prose caused exact policy IDs
      // such as SAMPLE-FLG-1003 to be truncated by the upstream name resolver.
      const { data } = await api.post("/copilot/carrier-intel", { question: q.trim().toLowerCase(), carrier, lang: "en" });
      setRes(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Carrier lookup unavailable.");
    } finally {
      setBusy(false);
    }
  };

  const r = res?.result;
  const money = (v) => (typeof v === "number" ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="carrier-truth-panel">
      <div className="mb-2 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-semibold">Carrier Truth</h3>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">read-only</span>
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder={`Client name or policy # (${carrier})`}
          data-testid="carrier-truth-input"
          className="h-9"
        />
        <Button onClick={run} disabled={busy} className="h-9 bg-[#17324D] text-white hover:bg-[#22456b]" data-testid="carrier-truth-search">
          <Search className="mr-1 h-4 w-4" />{busy ? "Checking…" : "Check"}
        </Button>
      </div>

      {err && <p className="mt-3 flex items-center gap-1 text-[13px] text-rose-600"><AlertTriangle className="h-4 w-4" />{err}</p>}

      {res && !r?.found && (
        <p className="mt-3 rounded-md bg-amber-50 p-2 text-[13px] text-amber-800" data-testid="carrier-truth-unverified">
          {r?.unverified_reason || res.reply || "Unable to verify from carrier source."}
        </p>
      )}

      {res && r?.found && (
        <div className="mt-3 space-y-2 text-[13px]" data-testid="carrier-truth-result">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{r.policyholder}</span>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">{r.normalized_status}</span>
            {r.returned_eft && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">NSF / Returned EFT</span>}
            {r.first_reminder && <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-[11px] font-semibold text-yellow-800">First Reminder</span>}
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
            <div>Policy #: <span className="font-mono text-foreground">{r.policy_number || r.application_number || "—"}</span></div>
            <div>Premium: <span className="text-foreground">{money(r.premium)} {r.billing_frequency}</span></div>
            <div>Face: <span className="text-foreground">{money(r.face_amount)}</span></div>
            <div>Effective: <span className="text-foreground">{r.effective_date || "—"}</span></div>
            <div>Paid-to: <span className="text-foreground">{r.paid_to_date || "—"}</span></div>
          </div>

          {/* Commission POSTED vs ACTUALLY DISBURSED — kept explicitly distinct */}
          <div className="rounded-md border border-border p-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Commission</div>
            <div className="flex flex-wrap gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.commission_posted ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600"}`} data-testid="commission-posted">
                Posted: {r.commission_posted ? money(r.commission_posted_amount) : "No"}
              </span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.commission_disbursed ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`} data-testid="commission-disbursed">
                Actually disbursed: {r.commission_disbursed ? `${money(r.commission_disbursed_amount)}${r.commission_disbursed_date ? " on " + r.commission_disbursed_date : ""}` : "No"}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">Posted (statement entry) and disbursed (money paid out) are different facts.</p>
          </div>

          {(r.additional_cases || []).map((c, i) => (
            <div key={i} className="rounded-md bg-slate-50 p-2 text-[12px]">
              Separate case {c.application_number || c.policy_number}: <span className="font-medium">{c.normalized_status}</span>
              {" — "}{c.pending_reason || "reason not available on the current screen"}
            </div>
          ))}

          <p className="text-[11px] text-muted-foreground">
            Source: {r.source} · confidence {r.confidence} {res.mode === "mock_read_only" ? "· (mock — live carrier access not yet configured)" : ""}
          </p>
        </div>
      )}
    </div>
  );
}
